import React from 'react';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Separator } from '../../../ui/separator';
import { Plus, X } from 'lucide-react';
import { formatSizeInput } from '../../../../lib/utils';
import { RATE_TYPES, PLYWOOD_SIZES, DOOR_SIZES_WITH_SQFT } from '../../constants';
import SizeBuilderMixed from '../../SizeBuilderMixed';

/**
 * Plywood-style bulk-thickness section.
 *
 * Rendered when the parent detects ``isPlywoodBulkMode`` — i.e. the current
 * ``main_category`` is one of the "sheet + thickness" categories (Plywood,
 * Blockboard, MDF, Doors, WPC/PVC, ACP). Lets the admin enter per-thickness
 * rates across every available thickness for the brand/subcategory in a
 * single form submission, plus tick which sheet sizes are stocked and add
 * custom thicknesses/sizes on the fly.
 */
export function PlywoodBulkSection({
  form,
  setForm,
  getAllThicknessesSorted,
  getAllSizesSorted,
  plywoodSizes,
  setPlywoodSizes,
  customSizes,
  setCustomSizes,
  customThicknesses,
  setCustomThicknesses,
  plywoodRates,
  setPlywoodRates,
  newThickness,
  setNewThickness,
  addCustomThickness,
  newSize,
  setNewSize,
  toggleSize,
}) {
  const addCustomSize = () => {
    const sizeValue = formatSizeInput(newSize).toLowerCase();
    if (sizeValue && sizeValue.includes('x')) {
      const parts = sizeValue.split('x');
      // For doors: sizes are in inches, so sqft = (h * w) / 144
      // For others: sizes are in feet, so sqft = h * w
      const sqft = form.main_category === 'Doors'
        ? Math.round((parseFloat(parts[0]) * parseFloat(parts[1]) / 144) * 100) / 100
        : Math.round(parseFloat(parts[0]) * parseFloat(parts[1]) * 10) / 10;
      const sizeId = sizeValue.replace(/\s/g, '');
      const displayLabel = form.main_category === 'Doors'
        ? `${parts[0].trim()}" x ${parts[1].trim()}"`
        : sizeValue.toUpperCase();
      const baseSizes = form.main_category === 'Doors' ? DOOR_SIZES_WITH_SQFT : PLYWOOD_SIZES;
      if (!baseSizes.find(s => s.id === sizeId) && !customSizes.find(s => s.id === sizeId)) {
        setCustomSizes([...customSizes, { id: sizeId, label: displayLabel, sqft }]);
        setPlywoodSizes([...plywoodSizes, sizeId]);
      }
      setNewSize('');
    }
  };

  return (
    <>
      <Separator />
      <div className="bg-heda-cream p-3 rounded-xl">
        <Label className="text-xs font-semibold uppercase tracking-wider text-heda-ink">
          Enter Rates per Sq. Ft (Leave empty if not available)
          {form.main_category && form.main_category !== 'Plywood' && (
            <span className="ml-2 text-heda-gold">- {form.main_category}</span>
          )}
        </Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
          {/* Sorted thicknesses: standard + custom in ascending order */}
          {getAllThicknessesSorted(form.main_category).map(t => {
            const isCustom = customThicknesses.includes(t.id);
            return (
              <div key={t.id} className="flex items-center gap-2">
                <span className={`text-xs font-medium w-20 ${isCustom ? 'text-heda-gold' : 'text-heda-sub'}`}>
                  {t.label}
                </span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Rate"
                  value={plywoodRates[t.id] || ''}
                  onChange={e => setPlywoodRates(r => ({ ...r, [t.id]: e.target.value }))}
                  className="rounded-xl h-8 text-base md:text-sm tabular-nums flex-1 bg-heda-cream/50"
                />
                {isCustom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full hover:bg-heda-cream h-8 w-8 p-0 text-red-700"
                    onClick={() => setCustomThicknesses(c => c.filter(x => x !== t.id))}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Add custom thickness */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-heda-line">
          <Input
            placeholder="Add custom thickness (e.g., 15.5MM)"
            value={newThickness}
            onChange={e => setNewThickness(e.target.value.toUpperCase())}
            className="rounded-xl h-8 text-base md:text-sm flex-1 bg-heda-cream/50"
            onKeyDown={e => e.key === 'Enter' && addCustomThickness()}
          />
          <Button variant="outline" size="sm" onClick={addCustomThickness} className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-8">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Size Selection - Sorted by area (largest first) */}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">
          Available Sizes {form.main_category === 'Doors' && <span className="text-heda-mute">(in inches)</span>}
        </Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {getAllSizesSorted().map(size => {
            const isCustom = customSizes.some(cs => cs.id === size.id);
            return (
              <label
                key={size.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                  plywoodSizes.includes(size.id)
                    ? isCustom
                      ? 'bg-heda-gold-tint text-heda-ink border-heda-gold'
                      : 'bg-heda-ink text-white border-heda-ink'
                    : isCustom
                      ? 'bg-white border-heda-gold/50 hover:bg-heda-gold-tint'
                      : 'bg-white border-heda-line hover:border-heda-gold'
                }`}
              >
                <input
                  type="checkbox"
                  checked={plywoodSizes.includes(size.id)}
                  onChange={() => toggleSize(size.id)}
                  className="hidden"
                />
                <span className="text-sm font-medium">{size.label}</span>
                <span className={`text-xs ${plywoodSizes.includes(size.id) ? 'text-white/70' : 'text-heda-mute'}`}>({size.sqft.toFixed(2)} sq.ft)</span>
                {isCustom && (
                  <button
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCustomSizes(customSizes.filter(s => s.id !== size.id));
                      setPlywoodSizes(plywoodSizes.filter(s => s !== size.id));
                    }}
                    className="ml-1 text-red-700 hover:text-red-700"
                  >
                    ×
                  </button>
                )}
              </label>
            );
          })}
        </div>

        {/* Per-dimension Unit Builder — pick numbers AND unit per
            side, e.g. 4ft × 6in. Builds a size string like "4ft x 6in" that
            `sizeToSqft` parses natively, and computes the sqft display via
            the same central helper so the catalog is always consistent
            with the cart math. Admin-only. */}
        <div className="mt-3 p-2 bg-heda-cream border border-heda-line rounded-xl" data-testid="size-builder-mixed">
          <p className="text-[10px] uppercase tracking-wide text-heda-sub mb-1.5">Size Builder (pick per-dimension units)</p>
          <SizeBuilderMixed
            onAdd={(sizeStr, sqftValue, label) => {
              const sizeId = sizeStr.replace(/\s/g, '').toLowerCase();
              if (!customSizes.find(s => s.id === sizeId)) {
                setCustomSizes([...customSizes, { id: sizeId, label, sqft: sqftValue }]);
                setPlywoodSizes([...plywoodSizes, sizeId]);
              }
            }}
          />
        </div>

        {/* Add Custom Size */}
        <div className="flex gap-2 mt-3">
          <Input
            value={newSize}
            onChange={e => setNewSize(formatSizeInput(e.target.value))}
            placeholder={form.main_category === 'Doors' ? 'Type size: 90 40 → 90x40' : 'Type size: 9.2 4 → 9.2x4'}
            className="rounded-xl flex-1 text-base md:text-sm bg-heda-cream/50"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomSize();
              }
            }}
            data-testid="custom-size-input"
          />
          <span className="text-[11px] text-heda-mute self-center whitespace-nowrap">Space → x</span>
          <Button
            variant="outline"
            onClick={addCustomSize}
            className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            data-testid="add-custom-size-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Rate Type for multi-thickness products */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Rate Type</Label>
          <Select value={form.rate_type || 'per sq ft'} onValueChange={v => setForm(f => ({ ...f, rate_type: v }))}>
            <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50" data-testid="form-rate-type-bulk"><SelectValue /></SelectTrigger>
            <SelectContent>{RATE_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {form.rate_type === 'per sq ft' && (
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Size Unit</Label>
            <Select value={form.size_unit || 'feet'} onValueChange={v => setForm(f => ({ ...f, size_unit: v }))}>
              <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50" data-testid="form-size-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feet">Feet</SelectItem>
                <SelectItem value="inches">Inches</SelectItem>
                <SelectItem value="mm">Millimeters (mm)</SelectItem>
                <SelectItem value="meters">Meters</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {form.rate_type === 'per box' && (
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Pieces per Box</Label>
            <Input
              type="number"
              placeholder="e.g., 100"
              value={form.pieces_per_box || ''}
              onChange={e => setForm(f => ({ ...f, pieces_per_box: parseInt(e.target.value, 10) || 0 }))}
              className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
              data-testid="form-pieces-per-box"
            />
          </div>
        )}
      </div>
    </>
  );
}
